import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { summariseMixByQueue, mktLabel } from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';

const TONE = {
  go: { bar: '#00C2C2', bg: '#E6F8F8', muted: '#0A1A2F' },
  watch: { bar: '#0A1A2F', bg: '#EEF1F4', muted: '#0A1A2F' },
  hold: { bar: '#5C6B7A', bg: '#F4F5F6', muted: '#5C6B7A' },
};

export default function MarketMixGrid({ summary, selectedQueue, onSelectQueue }) {
  const { t } = useTranslation();
  const mix = useMemo(() => summariseMixByQueue(summary), [summary]);
  const total = mix.reduce((s, m) => s + (m.count || 0), 0);
  const priority = summary?.mix_by_queue?.priority?.accounts;
  const funnelPriority = summary?.funnel?.priority ?? summary?.funnel?.priority_now;
  const mismatch = priority != null && funnelPriority != null && Number(priority) !== Number(funnelPriority);
  // [2026-10-25] A count disagreement is a diagnostic for us, not copy for a client. It printed
  // "These two counts should agree and do not. Please report it…" on blackbullion's Overview.
  React.useEffect(() => {
    if (mismatch) console.warn('[MarketMixGrid] priority count mismatch', { mix: priority, funnel: funnelPriority });
  }, [mismatch, priority, funnelPriority]);

  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader className="flex flex-row items-baseline justify-between space-y-0">
        <CardTitle className="text-base text-[#0A1A2F]">{t('mktMix')}</CardTitle>
        <p className="text-xs uppercase tracking-wide text-[#5C6B7A]">{t('mktNAccounts', { n: total })}</p>
      </CardHeader>
      <CardContent>
        {/* [2026-10-17] Before the summary arrives every count is 0, and this read "Nothing classified
            in this lens yet" on 4science's Priority view while 2,638 accounts were classified — a loading
            state presented as an empty market. Say it is loading until there is a summary to read. */}
        {summary == null ? (
          <p className="text-sm text-[#5C6B7A]">{t('mktLoadingEllipsis')}</p>
        ) : mix.every((d) => !d.count) ? (
          <p className="text-sm text-[#5C6B7A]">{t('mktNothingClassifiedYet')}</p>
        ) : (
          <div className="grid grid-cols-2 gap-px bg-[#0A1A2F]/10 border border-[#0A1A2F]/10">
            {mix.map((d) => {
              const tone = TONE[d.tone] || TONE.hold;
              const selected = selectedQueue === d.key;
              const empty = !d.count;
              return (
                <button
                  key={d.key}
                  type="button"
                  disabled={empty}
                  onClick={() => onSelectQueue?.(d.key)}
                  className={`text-left px-3 py-3 min-h-[72px] transition-shadow ${
                    empty ? 'cursor-default opacity-40' : 'hover:brightness-[0.98]'
                  } ${selected ? 'ring-2 ring-inset ring-[#00C2C2]' : ''}`}
                  style={{ background: tone.bg, borderLeft: `4px solid ${tone.bar}` }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: tone.muted }}>
                    {mktLabel(t, d.label)}
                  </p>
                  <p className="text-2xl font-semibold tabular-nums mt-1 text-[#0A1A2F]">{d.count}</p>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
